import { Button } from '@boltenergy-be/design-system'
import Card from 'components/card/Card'
import FixedPriceDrawer from 'features/registration/components/registration-side-card/sales-prices-section/FixedPriceDrawer'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { store } from 'store'
import { useGetProducersQuery } from 'store/api/bolt-api'
import { updateOpportunityId, setSelectedProducer } from 'store/app/slice'
import { CustomerFlows, InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import {
  goToStep,
  resetCustomerStore,
  setIsExtraAddressFlow,
  setSelectedExtraAddressAccount,
  updateFixedPrices,
  updateInput,
  updateSimulatedPropositions
} from 'store/customer/slice'
import { Language } from 'types/language'
import { filterAndSortProducers } from 'utils/producers'
import { routes } from 'utils/routes'
import { resetPostHogUser } from 'utils/tracking'
import styles from './SimulationTypeToggle.module.scss'
import { useTranslation } from 'next-i18next/pages'
import { SimulationSteps } from 'features/simulation/types'
import { SimulationType, SIMULATION_TYPES } from '@boltenergy-be/shared'

const SimulationTypeToggle = () => {
  // Next Router
  const router = useRouter()
  const { locale, pathname } = router

  // Local state
  const [showSimSelection, setShowSimSelection] = useState<boolean>(pathname === '/')
  const [showFixedPriceDrawer, setShowFixedPriceDrawer] = useState<boolean>(false)

  // Redux store
  const { data: producers } = useGetProducersQuery()
  const dispatch = useStoreDispatch()
  const { simulationType, resetButton } = useStoreSelector((store) => store.marketing)
  const { chosenSimulationType } = useStoreSelector((store) => store.customer.inputs.simulation)

  // i18n
  const { t } = useTranslation(['common', 'seo'])

  /**
   * Handles the simulation type switch
   *
   * @param {SimulationType} desiredSimulationType
   */
  const handleSimulationSwitch = (desiredSimulationType: SimulationType) => {
    setShowSimSelection(false)

    if (desiredSimulationType === SIMULATION_TYPES.fixedAmount) {
      setShowFixedPriceDrawer(true)
    }

    if (chosenSimulationType === SIMULATION_TYPES.fixedAmount) {
      store.dispatch(updateFixedPrices(undefined))
    } else if (chosenSimulationType === SIMULATION_TYPES.pricingSimulation) {
      store.dispatch(updateSimulatedPropositions({ propositions: null })) //default value of simulatedPropositions is null
    }

    //update store with chosen simulation type
    store.dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
        value: desiredSimulationType
      })
    )

    //if desired simulation type is pricing simulation, redirect to first step of pricing simulation
    if (desiredSimulationType === SIMULATION_TYPES.pricingSimulation) {
      router.push(routes(locale).simulation)
      store.dispatch(goToStep({ step: SimulationSteps.POSTAL_CODE, flow: CustomerFlows.SIMULATION }))
    }
  }

  /**
   * Handles the reset button onClick event
   */
  const handleOnResetButtonClick = async () => {
    // redirect to homepage
    await router.push(routes(locale).home)

    // reset opportunityId, customer store & selected producer
    dispatch(updateOpportunityId({ opportunityId: '' }))
    resetPostHogUser()
    dispatch(resetCustomerStore(chosenSimulationType))

    const defaultProducer = filterAndSortProducers({
      hideSoldOut: true,
      language: locale as Language,
      producers: Object.values(producers)
    })[0]
    dispatch(setSelectedProducer({ producerSlug: defaultProducer.slug }))

    // Clear extra address flow state
    dispatch(setIsExtraAddressFlow(false))
    dispatch(setSelectedExtraAddressAccount(undefined))
  }

  return (
    <div className={styles['reset-button']}>
      {resetButton && simulationType.length === 1 ? (
        <Button variant="secondary" onClick={async () => await handleOnResetButtonClick()} leadingIcon="refresh">
          {t('optionsButton.reset')}
        </Button>
      ) : (
        <>
          <Button
            variant={showSimSelection ? 'layered' : 'secondary'}
            onClick={() => setShowSimSelection(!showSimSelection)}
            leadingIcon={showSimSelection ? 'cross' : 'settings'}
          >
            {t('optionsButton.title', 'Opties')}
          </Button>

          {showSimSelection && (
            <Card className={styles['button-card']}>
              {Object.keys(SIMULATION_TYPES).map((key) => {
                if (simulationType.includes(SIMULATION_TYPES[key])) {
                  return (
                    <Button
                      key={key}
                      variant="tertiary"
                      leadingIcon="shuffle"
                      isFullwidth
                      onClick={() => handleSimulationSwitch(SIMULATION_TYPES[key])}
                      disabled={chosenSimulationType === SIMULATION_TYPES[key]}
                      data-selected={chosenSimulationType === SIMULATION_TYPES[key]}
                      size="small"
                    >
                      {t(`optionsButton.${key}`)}
                      {chosenSimulationType === SIMULATION_TYPES[key] ? ` (${t('optionsButton.active')})` : ''}
                    </Button>
                  )
                }
              })}

              {resetButton && (
                <Button
                  variant="tertiary"
                  size="small"
                  isFullwidth
                  onClick={async () => await handleOnResetButtonClick()}
                  leadingIcon="refresh"
                >
                  {t('optionsButton.reset')}
                </Button>
              )}
            </Card>
          )}
        </>
      )}
      <FixedPriceDrawer isOpen={showFixedPriceDrawer} onRequesteClose={() => setShowFixedPriceDrawer(false)} />
    </div>
  )
}

export default SimulationTypeToggle
